import React, { useEffect, useRef, useState } from 'react';
import './About.css';
import aultraBoy from '../../assets/logo/aultralogo.jpg';
import chairman from '../../assets/chairman.JPG';
import Timeline from './Timeline';

const About = () => {
  const [yearsCount, setYearsCount] = useState(0);
  const [dealersCount, setDealersCount] = useState(0);
  const [shadesCount, setShadesCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  
  const statsRef = useRef(null);
  const storyTitleRef = useRef(null);
  const storyParagraphsRef = useRef([]);
  const visionCardRef = useRef(null);
  const missionCardRef = useRef(null);

  // Counter animation
  useEffect(() => {
    const currentStatsRef = statsRef.current;
    
    const observerOptions = {
      threshold: 0.5,
      rootMargin: '0px'
    };

    const observerCallback = (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          
          // Animate years (20)
          let yearsStart = 0;
          const yearsEnd = 20;
          const yearsDuration = 2000;
          const yearsIncrement = yearsEnd / (yearsDuration / 16);
          
          const yearsTimer = setInterval(() => {
            yearsStart += yearsIncrement;
            if (yearsStart >= yearsEnd) {
              setYearsCount(yearsEnd);
              clearInterval(yearsTimer);
            } else {
              setYearsCount(Math.floor(yearsStart));
            }
          }, 16);

          // Animate dealers (2300)
          let dealersStart = 0;
          const dealersEnd = 2300;
          const dealersDuration = 2000;
          const dealersIncrement = dealersEnd / (dealersDuration / 16);
          
          const dealersTimer = setInterval(() => {
            dealersStart += dealersIncrement;
            if (dealersStart >= dealersEnd) {
              setDealersCount(dealersEnd);
              clearInterval(dealersTimer);
            } else {
              setDealersCount(Math.floor(dealersStart));
            }
          }, 16);

          // Animate shades (1800)
          let shadesStart = 0;
          const shadesEnd = 1800;
          const shadesDuration = 2000;
          const shadesIncrement = shadesEnd / (shadesDuration / 16);
          
          const shadesTimer = setInterval(() => {
            shadesStart += shadesIncrement;
            if (shadesStart >= shadesEnd) {
              setShadesCount(shadesEnd);
              clearInterval(shadesTimer);
            } else {
              setShadesCount(Math.floor(shadesStart));
            }
          }, 16);
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);
    
    if (currentStatsRef) {
      observer.observe(currentStatsRef);
    }

    return () => {
      if (currentStatsRef) {
        observer.unobserve(currentStatsRef);
      }
    };
  }, [hasAnimated]);

  // Story section animations - triggers every time
  useEffect(() => {
    const currentStoryTitleRef = storyTitleRef.current;
    const currentStoryParagraphsRef = storyParagraphsRef.current;
    
    const observerOptions = {
      threshold: 0.3,
      rootMargin: '0px'
    };

    const titleCallback = (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('about__story-title--visible');
        } else {
          entry.target.classList.remove('about__story-title--visible');
        }
      });
    };

    const paragraphCallback = (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('about__story-paragraph--visible');
        } else {
          entry.target.classList.remove('about__story-paragraph--visible');
        }
      });
    };

    const titleObserver = new IntersectionObserver(titleCallback, observerOptions);
    const paragraphObserver = new IntersectionObserver(paragraphCallback, observerOptions);

    if (currentStoryTitleRef) {
      titleObserver.observe(currentStoryTitleRef);
    }

    currentStoryParagraphsRef.forEach((paragraph) => {
      if (paragraph) {
        paragraphObserver.observe(paragraph);
      }
    });

    return () => {
      if (currentStoryTitleRef) {
        titleObserver.unobserve(currentStoryTitleRef);
      }
      currentStoryParagraphsRef.forEach((paragraph) => {
        if (paragraph) {
          paragraphObserver.unobserve(paragraph);
        }
      });
    };
  }, []);

  // Vision & Mission cards animations - triggers every time
  useEffect(() => {
    const currentVisionCardRef = visionCardRef.current;
    const currentMissionCardRef = missionCardRef.current;
    
    const observerOptions = {
      threshold: 0.3,
      rootMargin: '0px'
    };

    const cardCallback = (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('about__vm-card--visible');
        } else {
          entry.target.classList.remove('about__vm-card--visible');
        }
      });
    };

    const cardObserver = new IntersectionObserver(cardCallback, observerOptions);

    if (currentVisionCardRef) {
      cardObserver.observe(currentVisionCardRef);
    }

    if (currentMissionCardRef) {
      cardObserver.observe(currentMissionCardRef);
    }

    return () => {
      if (currentVisionCardRef) {
        cardObserver.unobserve(currentVisionCardRef);
      }
      if (currentMissionCardRef) {
        cardObserver.unobserve(currentMissionCardRef);
      }
    };
  }, []);

  return (
    <>
      <head>
        <title>About Us - Aultra Paints | Leading Paint Company in India Since 2005</title>
        <meta 
          name="description" 
          content="Discover Aultra Paints' 20-year journey from spray plasters to premium decorative paints. Learn about our vision, mission, and commitment to quality paint solutions for Indian homes." 
        />
        <meta 
          name="keywords" 
          content="Aultra Paints, decorative paints, interior paints, exterior paints, paint company India, quality paints, eco-friendly paints, Devaram SaidiReddy" 
        />
        <meta name="author" content="Aultra Paints Pvt. Ltd." />
        <meta property="og:title" content="About Aultra Paints - Premium Paint Solutions Since 2005" />
        <meta 
          property="og:description" 
          content="Learn about Aultra Paints' journey, vision, and commitment to transforming spaces with high-quality, eco-friendly paint solutions." 
        />
        <meta property="og:type" content="website" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://aultrapaints.com/about" />
      </head>

      <main className="about">
        {/* Hero Section */}
        <section className="about__hero">
          <div className="about__hero-container container">
            <div className="about__hero-content">
              <span className="chip chip--accent">Since 2005</span>
              <h1 className="about__hero-title">About Aultra Paints</h1>
              <p className="about__hero-subtitle">
                Transforming spaces with vibrant colors for two decades
              </p>
              
              {/* Stats moved here */}
              <ul className="about__hero-stats" ref={statsRef}>
                <li className="about__hero-stat">
                  <span className="about__hero-stat-num">{yearsCount}+</span>
                  <span className="about__hero-stat-label">Years Legacy</span>
                </li>
                <li className="about__hero-stat">
                  <span className="about__hero-stat-num">{dealersCount}+</span>
                  <span className="about__hero-stat-label">Dealer Partners</span>
                </li>
                <li className="about__hero-stat">
                  <span className="about__hero-stat-num">{shadesCount}+</span>
                  <span className="about__hero-stat-label">Shades Crafted</span>
                </li>
              </ul>

              <div className="about__cta">
                <a href="/products" className="btn btn--primary">Explore Products</a>
                <a href="/catalogue" className="btn btn--ghost">Color Catalogue</a>
              </div>
            </div>
            <div className="about__hero-image fade-in">
              <div className="blob">
                <img 
                  src={aultraBoy} 
                  alt="Aultra Paints mascot representing quality paint solutions" 
                  className="about__mascot-image"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Company Story */}
        <section className="about__story section">
          <div className="container">
            <div className="card card--glass">
              <div className="about__story-content">
                <h2 className="about__section-title about__story-title" ref={storyTitleRef}>Our Story</h2>
                <p 
                  className="about__story-text about__story-paragraph" 
                  ref={(el) => (storyParagraphsRef.current[0] = el)}
                >
                  AultraTek, a well-established name in the paint industry, started its journey in 2005 with spray plasters. Over the last two decades, our focus on customer satisfaction and constant innovation has helped us grow into Aultra Paints, offering a wide range of premium products.
                </p>
                <p 
                  className="about__story-text about__story-paragraph" 
                  ref={(el) => (storyParagraphsRef.current[1] = el)}
                >
                  Homes reflect personality and values, so our interior and exterior shades are crafted to make spaces warm and inviting. With a new state-of-the-art manufacturing facility and products designed for Indian weather, we're redefining color experiences for modern homes.
                </p>
              </div>
            </div>
          </div>
        </section>
                <Timeline/>


        {/* Vision & Mission */}
        <section className="about__vision-mission section">
          <div className="container">
            <div className="about__vm-grid">
              <div 
                className="about__vision-card card hover-up about__vision-card--animate" 
                ref={visionCardRef}
              >
                <div className="about__card-icon">
                  <span className="about__icon-vision" aria-hidden="true">👁️</span>
                </div>
                <h3 className="about__card-title">Our Vision</h3>
                <p className="about__card-text">
                  To be one of India's most trusted and preferred decorative paint brands, leading with innovation, environmental responsibility, and partner-first growth.
                </p>
                <div className="accent-bar" />
              </div>

              <div 
                className="about__mission-card card hover-up about__mission-card--animate" 
                ref={missionCardRef}
              >
                <div className="about__card-icon">
                  <span className="about__icon-mission" aria-hidden="true">🎯</span>
                </div>
                <h3 className="about__card-title">Our Mission</h3>
                <p className="about__card-text">
                  Transform spaces with vibrant colors and durable finishes through innovative, eco-friendly solutions and a customer-first approach.
                </p>
                <div className="accent-bar" />
              </div>
            </div>
          </div>
        </section>

        {/* Chairman Message */}
        <section className="about__chairman section">
          <div className="container">
            <div className="about__chairman-content card card--panel hover-up">
              <div className="about__chairman-image">
                <img 
                  src={chairman} 
                  alt="Devaram SaidiReddy, Chairman of Aultra Paints Pvt. Ltd." 
                  className="about__chairman-photo"
                  loading="lazy"
                />
              </div>
              <div className="about__chairman-text">
                <h2 className="about__section-title">Message from the Chairman</h2>
                <blockquote className="about__chairman-quote">
                  "At Aultra Paints, we are driven by a passion for innovation, sustainability, and quality. From humble beginnings to becoming a trusted name in decorative paints across India, our mission is to transform spaces with vibrant colors and durable finishes. With a strong focus on eco-friendly solutions and customer satisfaction, we continue to build a legacy of excellence—guided by integrity, service, and a commitment to a brighter, more colorful future."
                </blockquote>
                <div className="about__chairman-details">
                  <h4 className="about__chairman-name">Devaram SaidiReddy</h4>
                  <p className="about__chairman-title">Chairman, Aultra Paints Pvt. Ltd.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Company Values */}
        <section className="about__values section">
          <div className="container">
            <h2 className="about__section-title center">Our Core Values</h2>
            <div className="about__values-grid">
              <div className="about__value-item card hover-float">
                <div className="about__value-icon">🏆</div>
                <h3 className="about__value-title">Quality</h3>
                <p className="about__value-text">Premium products tailored for Indian weather conditions</p>
              </div>
              <div className="about__value-item card hover-float">
                <div className="about__value-icon">💰</div>
                <h3 className="about__value-title">Affordability</h3>
                <p className="about__value-text">Premium quality paints at pocket-friendly prices</p>
              </div>
              <div className="about__value-item card hover-float">
                <div className="about__value-icon">💡</div>
                <h3 className="about__value-title">Innovation</h3>
                <p className="about__value-text">Continuous innovation in paint technology</p>
              </div>
              <div className="about__value-item card hover-float">
                <div className="about__value-icon">🤝</div>
                <h3 className="about__value-title">Trust</h3>
                <p className="about__value-text">Building lasting relationships through transparency</p>
              </div>
              <div className="about__value-item card hover-float">
                <div className="about__value-icon">🌱</div>
                <h3 className="about__value-title">Eco-Friendly</h3>
                <p className="about__value-text">Sustainable and environmentally responsible paint solutions</p>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default About;